import { createAsyncThunk } from "@reduxjs/toolkit";
import { API_ENDPOINTS } from "../../shared/constants/constants";
import { waitForGoogleMaps } from "../../shared/utils/googleMapsUtils";

interface ChatRequest {
    query: string;
    orgPartyName: string;
    userLocation?: {
        latitude: number;
        longitude: number;
        city?: string | null;
        state?: string | null;
        country?: string | null;
        formattedAddress?: string | null;
    };
    agent_type?: string;
}

interface LocationDetails {
    state: string;
    country: string;
    city: string;
    formattedAddress: string;
}

interface LocationResponse {
    data?: LocationDetails;
    error?: string;
    status: number;
}

// Type guards for better type safety
function isSuccessResponse(response: LocationResponse): response is LocationResponse & { data: LocationDetails } {
    return 'data' in response && !!response.data;
}

function isErrorResponse(response: LocationResponse): response is LocationResponse & { error: string } {
    return 'error' in response && !!response.error;
}

export const sendChatMessage = createAsyncThunk(
    'discoveryAgent/sendChatMessage',
    async (requestData: ChatRequest) => {
        try {
            const isEnterprise = requestData.agent_type === "enterprise";
            
            // For enterprise mode, use the original query without location enhancement
            // For normal mode, add location information to the query if available
            let enhancedQuery = requestData.query;
            if (!isEnterprise && requestData.userLocation) {
                let locationString = "";
                if (requestData.userLocation.city && requestData.userLocation.state && requestData.userLocation.country) {
                    locationString = `${requestData.userLocation.city}, ${requestData.userLocation.state}, ${requestData.userLocation.country}`;
                } else if (requestData.userLocation.state && requestData.userLocation.country) {
                    locationString = `${requestData.userLocation.state}, ${requestData.userLocation.country}`;
                } else if (requestData.userLocation.country) {
                    locationString = requestData.userLocation.country;
                }

                if (locationString) {
                    enhancedQuery = `${requestData.query}\tUser's location: ${locationString}`;
                }
            }

            // Add formatting instructions only for normal mode
            if (!isEnterprise) {
                enhancedQuery =
                    enhancedQuery +
                    "\n\nPlease provide references to all the links. Please return the output in jsx format, I am using it on frontend, should directly merge with the existing code, put it inside a div tag. Also return the product details in proper table tag with proper formatting and headings.";
            }

            // Determine the API endpoint based on mode
            const apiEndpoint = isEnterprise 
                ? `${API_ENDPOINTS.discoveryAgentChat.replace('/product-assistant/', '/product-assistant-enterprise/')}`
                : API_ENDPOINTS.discoveryAgentChat;
            
            // Prepare request payload
            const requestPayload = {
                query: enhancedQuery,
                orgPartyName: requestData.orgPartyName,
                ...(requestData.userLocation && {
                    userLocation: requestData.userLocation,
                }),
            };

            const response = await fetch(apiEndpoint, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                },
                body: JSON.stringify(requestPayload),
            });

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const data = await response.json();
            
            // Handle different response formats for normal vs enterprise mode
            let responseText;
            if (isEnterprise) {
                // Enterprise response format: data.response.output
                responseText = data?.response?.output
                    ?.replaceAll(/```jsx\s*/g, "")
                    ?.replaceAll(/```\s*/g, "")
                    ?.replaceAll(/jsx\s*/g, "")
                    ?.replaceAll(/`/g, "")
                    ?.trim() || "Sorry, I could not find a relevant answer.";
            } else {
                // Normal response format: data.response.output.output
                responseText = data?.response?.output?.output
                    ?.replaceAll(/```jsx\s*/g, "")
                    ?.replaceAll(/```\s*/g, "")
                    ?.replaceAll(/jsx\s*/g, "")
                    ?.replaceAll(/`/g, "")
                    ?.trim() || "Sorry, I could not find a relevant answer.";
            }

            return {
                data: responseText,
                status: response.status
            };
        } catch (error: any) {
            console.error("Error sending chat message:", error);
            return {
                error: error.message || "Sorry, there was an error processing your request. Please try again.",
                status: error.status || 500,
            };
        }
    }
);

export const getLocationFromCoordinates = createAsyncThunk(
    'discoveryAgent/getLocationFromCoordinates',
    async ({ latitude, longitude }: { latitude: number; longitude: number }) => {
        try {
            // Wait for Google Maps API to be loaded
            await waitForGoogleMaps();

            return new Promise<LocationResponse>((resolve) => {
                const geocoder = new window.google.maps.Geocoder();
                const latlng = { lat: latitude, lng: longitude };

                geocoder.geocode({ location: latlng }, (results: any, status: any) => {
                    if (status === window.google.maps.GeocoderStatus.OK && results && results.length > 0) {
                        const addressComponents = results[0].address_components;
                        let country = "";
                        let state = "";
                        let city = "";

                        // Extract state, city, and country from address components
                        for (const component of addressComponents) {
                            if (component.types.includes("country")) {
                                country = component.long_name;
                            }
                            if (component.types.includes("administrative_area_level_1")) {
                                state = component.long_name;
                            }
                            // Look for city in locality or administrative_area_level_2
                            if (component.types.includes("locality")) {
                                city = component.long_name;
                            } else if (
                                !city &&
                                component.types.includes("administrative_area_level_2")
                            ) {
                                city = component.long_name;
                            } else if (!city && component.types.includes("sublocality_level_1")) {
                                city = component.long_name;
                            }
                        }

                        const details: LocationDetails = {
                            state,
                            country,
                            city,
                            formattedAddress: results[0].formatted_address,
                        };

                        resolve({
                            data: details,
                            status: 200
                        });
                    } else {
                        resolve({
                            error: "No address found for these coordinates",
                            status: 404
                        });
                    }
                });
            });
        } catch (error: any) {
            console.error("Error getting location:", error);
            return {
                error: error.message || "Failed to get location details",
                status: error.status || 500,
            } as LocationResponse;
        }
    }
); 